import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, FileDown, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { Badge, Select, fmtDT, fmtD } from "@/components/admin/ui";

const STATUSES = ["new", "reviewed", "approved", "ordered", "delivered", "completed"];

export function RestockList({ items, canApprove, onChange, showStore }) {
  const [openId, setOpenId] = useState(null);
  const base = api.defaults.baseURL;

  const setStatus = async (r, status) => {
    try { await api.patch(`/restock-orders/${r.id}`, { status }); toast.success(`Marked ${status}`); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/restock-orders/${r.id}`); toast.success("Deleted"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!items.length) return <p className="p-10 text-center text-neutral-500 text-sm" data-testid="restock-empty">No restock requests.</p>;

  return (
    <div className="divide-y divide-neutral-100">
      {items.map((r) => {
        const expanded = openId === r.id;
        return (
          <div key={r.id} className="p-4" data-testid={`restock-row-${r.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => setOpenId(expanded ? null : r.id)} className="flex-1 min-w-[200px] text-left" data-testid={`restock-toggle-${r.id}`}>
                <div className="font-medium flex items-center gap-2">
                  {showStore && <span className="text-[hsl(var(--primary))]">{r.store_name}</span>}
                  <span>{r.items.length} item{r.items.length !== 1 ? "s" : ""}</span>
                  <span className="text-neutral-400 font-normal text-xs">— {r.items.slice(0, 3).map((i) => i.name).join(", ")}{r.items.length > 3 ? "…" : ""}</span>
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">{r.requested_by_name} · {fmtDT(r.created_at)}{r.needed_by ? ` · needed by ${fmtD(r.needed_by)}` : ""}</div>
              </button>
              <Badge data-testid={`restock-priority-${r.id}`}>{r.priority}</Badge>
              {canApprove ? (
                <Select value={r.status} onChange={(e) => setStatus(r, e.target.value)} className="h-8 text-xs" data-testid={`restock-status-${r.id}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                </Select>
              ) : <Badge data-testid={`restock-status-${r.id}`}>{r.status}</Badge>}
              <a href={`${base}/restock-orders/${r.id}/export?format=pdf`} target="_blank" rel="noreferrer" title="Download PDF" data-testid={`restock-pdf-${r.id}`}><Button size="sm" variant="outline"><FileText className="w-3.5 h-3.5 mr-1"/>PDF</Button></a>
              <a href={`${base}/restock-orders/${r.id}/export?format=docx`} title="Download Word" data-testid={`restock-docx-${r.id}`}><Button size="sm" variant="outline"><FileDown className="w-3.5 h-3.5 mr-1"/>Word</Button></a>
              {canApprove && <Button size="sm" variant="ghost" onClick={() => remove(r)} className="text-red-600"><Trash2 className="w-3.5 h-3.5"/></Button>}
              <button onClick={() => setOpenId(expanded ? null : r.id)} className="text-neutral-400">{expanded ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}</button>
            </div>
            {expanded && (
              <div className="mt-3 grid md:grid-cols-[1fr_260px] gap-4" data-testid={`restock-detail-${r.id}`}>
                <table className="text-sm w-full"><tbody className="divide-y divide-neutral-100">
                  {r.items.map((it, i) => <tr key={i}><td className="py-1.5 font-medium">{it.name}</td><td className="py-1.5 text-right whitespace-nowrap">{it.quantity} {it.unit}</td><td className="py-1.5 pl-4 text-neutral-500">{it.notes}</td></tr>)}
                </tbody></table>
                <div className="text-xs text-neutral-500 space-y-1">
                  {r.notes && <p><b>Notes:</b> {r.notes}</p>}
                  <p className="font-semibold text-neutral-700 mt-2">History</p>
                  {(r.status_history || []).map((h, i) => <p key={i}><Badge>{h.status}</Badge> {fmtDT(h.at)} · {h.by_name}</p>)}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
